import { createElement, type ReactNode } from "react";
import {
  tokenizeInlineText,
  type InlineTextTag,
} from "@docid/types/src/inlineText";

type NodeBuilder = {
  children: ReactNode[];
  tag: InlineTextTag | null;
};

export function InlineText({ value }: { value: null | string | undefined }) {
  if (!value) {
    return null;
  }

  const root: NodeBuilder = { children: [], tag: null };
  const stack = [root];
  let key = 0;

  const appendElement = (builder: NodeBuilder) => {
    stack
      .at(-1)
      ?.children.push(
        createElement(
          builder.tag as InlineTextTag,
          { key: key++ },
          builder.children,
        ),
      );
  };

  for (const token of tokenizeInlineText(value)) {
    const current = stack.at(-1) as NodeBuilder;

    if (token.type === "text") {
      current.children.push(token.value);
      continue;
    }

    if (token.type === "break") {
      current.children.push(createElement("br", { key: key++ }));
      continue;
    }

    if (token.type === "open") {
      stack.push({ children: [], tag: token.tag });
      continue;
    }

    if (current.tag === token.tag && stack.length > 1) {
      appendElement(stack.pop() as NodeBuilder);
    } else {
      current.children.push(token.raw);
    }
  }

  while (stack.length > 1) {
    appendElement(stack.pop() as NodeBuilder);
  }

  // Preserve the existing DOM for plain values. Only values split across
  // multiple root nodes need a wrapper so flex gaps cannot separate text from
  // inline markup such as a registered mark.
  if (root.children.length === 1) {
    return root.children[0];
  }

  return createElement("span", null, ...root.children);
}
